import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $findMatchingParent, mergeRegister } from '@lexical/utils';
import {
  $createRangeSelection,
  $createParagraphNode,
  $createTextNode,
  $getNodeByKey,
  $getPreviousSelection,
  $getRoot,
  $getSelection,
  $isElementNode,
  $isParagraphNode,
  $isRangeSelection,
  $isRootNode,
  $isTextNode,
  $setSelection,
  COMMAND_PRIORITY_LOW,
  IS_BOLD,
  createCommand,
  DELETE_CHARACTER_COMMAND,
  INSERT_PARAGRAPH_COMMAND,
  KEY_ARROW_DOWN_COMMAND,
  KEY_ARROW_LEFT_COMMAND,
  KEY_ARROW_RIGHT_COMMAND,
  KEY_ARROW_UP_COMMAND,
  KEY_BACKSPACE_COMMAND,
} from 'lexical';
import { useEffect } from 'react';

import {
  $createCollapsibleContainerNode,
  $isCollapsibleContainerNode,
  CollapsibleContainerNode,
} from '../nodes/CollapsibleContainerNode';
import {
  $createCollapsibleContentNode,
  $isCollapsibleContentNode,
  CollapsibleContentNode,
} from '../nodes/CollapsibleContentNode';
import {
  $createCollapsibleTitleNode,
  $isCollapsibleTitleNode,
  CollapsibleTitleNode,
} from '../nodes/CollapsibleTitleNode';

export const INSERT_COLLAPSIBLE_COMMAND = createCommand();
export const TOGGLE_COLLAPSIBLE_COMMAND = createCommand();

export default function CollapsiblePlugin() {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    if (
      !editor.hasNodes([CollapsibleContainerNode, CollapsibleTitleNode, CollapsibleContentNode])
    ) {
      throw new Error(
        'CollapsiblePlugin: CollapsibleContainerNode, CollapsibleTitleNode, or CollapsibleContentNode not registered on editor',
      );
    }

    const onEscapeUp = () => {
      const selection = $getSelection();
      if (
        $isRangeSelection(selection) &&
        selection.isCollapsed() &&
        selection.anchor.offset === 0
      ) {
        const container = $findMatchingParent(
          selection.anchor.getNode(),
          $isCollapsibleContainerNode,
        );

        if ($isCollapsibleContainerNode(container)) {
          const parent = container.getParent();
          if (
            parent !== null &&
            parent.getFirstChild() === container &&
            selection.anchor.key === container.getFirstDescendant()?.getKey()
          ) {
            container.insertBefore($createParagraphNode());
          }
        }
      }

      return false;
    };

    const onEscapeDown = () => {
      const selection = $getSelection();
      if ($isRangeSelection(selection) && selection.isCollapsed()) {
        const container = $findMatchingParent(
          selection.anchor.getNode(),
          $isCollapsibleContainerNode,
        );

        if ($isCollapsibleContainerNode(container)) {
          const titleParagraph = container.getFirstDescendant();
          const contentNode = container.getLastChild();
          const contentParagraph = container.getLastDescendant();

          if (
            titleParagraph !== null &&
            selection.anchor.key === titleParagraph.getKey() &&
            selection.anchor.offset === titleParagraph.getTextContentSize() &&
            $isCollapsibleContentNode(contentNode)
          ) {
            if (!container.getOpen()) {
              container.setOpen(true);
            }

            const firstBlock = contentNode.getFirstChild();
            if ($isElementNode(firstBlock)) {
              firstBlock.selectStart();
              // Clear the bold/other formats carried over from the title so
              // content typing starts at the content's default (non-bold).
              const contentSelection = $getSelection();
              if ($isRangeSelection(contentSelection)) {
                contentSelection.setFormat(0);
              }
              return true;
            }
          }

          const parent = container.getParent();
          if (parent !== null && parent.getLastChild() === container) {
            if (
              contentParagraph !== null &&
              selection.anchor.key === contentParagraph.getKey() &&
              selection.anchor.offset === contentParagraph.getTextContentSize()
            ) {
              container.insertAfter($createParagraphNode());
            }
          }
        }
      }

      return false;
    };

    const onBackspace = event => {
      const selection = $getSelection();
      if (
        !$isRangeSelection(selection) ||
        !selection.isCollapsed() ||
        selection.anchor.offset !== 0
      ) {
        return false;
      }

      const consume = () => {
        // Prevent the browser's native backspace from running after our
        // handler. Without this, Chrome can drift the DOM selection (e.g.
        // into the previous collapsible) and then fire a beforeinput
        // deleteContentBackward that re-dispatches DELETE_CHARACTER_COMMAND
        // from that drifted position.
        if (event && typeof event.preventDefault === 'function') {
          event.preventDefault();
        }
        return true;
      };

      const anchorNode = selection.anchor.getNode();

      // Backspace on an empty collapsible title → remove the container and
      // hoist any content blocks back out in its place. The title is created
      // with bold pre-enabled, so we also explicitly clear the selection
      // format afterwards to avoid the next keystroke typing in bold.
      const titleAncestor = $findMatchingParent(anchorNode, $isCollapsibleTitleNode);
      if ($isCollapsibleTitleNode(titleAncestor) && titleAncestor.getTextContent() === '') {
        const titleContainer = titleAncestor.getParent();
        if ($isCollapsibleContainerNode(titleContainer)) {
          const titleContentNode = titleContainer.getLastChild();
          const contentBlocks = $isCollapsibleContentNode(titleContentNode)
            ? titleContentNode.getChildren()
            : [];
          const hoisted = contentBlocks.length > 0 ? contentBlocks : [$createParagraphNode()];
          for (const block of hoisted) {
            titleContainer.insertBefore(block);
          }
          titleContainer.remove();
          hoisted[0].selectStart();
          const clearedSelection = $getSelection();
          if ($isRangeSelection(clearedSelection)) {
            clearedSelection.setFormat(0);
          }
          return consume();
        }
      }

      const contentNode = $findMatchingParent(anchorNode, $isCollapsibleContentNode);
      if (!$isCollapsibleContentNode(contentNode)) {
        return false;
      }

      // Walk up until we find the direct child of the content node.
      let topLevelInContent = anchorNode;
      while (topLevelInContent && topLevelInContent.getParent() !== contentNode) {
        topLevelInContent = topLevelInContent.getParent();
      }
      if (!topLevelInContent) {
        return false;
      }

      const container = contentNode.getParent();
      if (!$isCollapsibleContainerNode(container)) {
        return false;
      }
      const titleNode = container.getFirstChild();
      if (!$isCollapsibleTitleNode(titleNode)) {
        return false;
      }

      const firstContentChild = contentNode.getFirstChild();
      const lastContentChild = contentNode.getLastChild();

      // Backspace at the start of the first line of content → caret to title.
      // No characters are deleted; this mirrors the up-arrow escape.
      if (topLevelInContent === firstContentChild) {
        if (!container.getOpen()) {
          container.setOpen(true);
        }
        const titleBlock = titleNode.getLastChild();
        if ($isElementNode(titleBlock)) {
          titleBlock.selectEnd();
        } else {
          titleNode.selectEnd();
        }
        return consume();
      }

      // Backspace on the empty last line (when content has multiple lines) →
      // remove that line and drop the caret into a paragraph below the
      // container so the user can keep typing outside the collapsible.
      if (
        topLevelInContent === lastContentChild &&
        contentNode.getChildrenSize() > 1 &&
        topLevelInContent.getTextContent() === ''
      ) {
        topLevelInContent.remove();
        let afterParagraph = container.getNextSibling();
        if (!$isParagraphNode(afterParagraph)) {
          afterParagraph = $createParagraphNode();
          container.insertAfter(afterParagraph);
        }
        afterParagraph.selectStart();
        return consume();
      }

      return false;
    };

    return mergeRegister(
      // Structure enforcing transformers for each node type. In case nesting structure is not
      // "Container > Title + Content" it'll unwrap nodes and convert it back
      // to regular content.
      editor.registerNodeTransform(CollapsibleContentNode, node => {
        const parent = node.getParent();
        if (!$isCollapsibleContainerNode(parent)) {
          const children = node.getChildren();
          for (const child of children) {
            node.insertBefore(child);
          }
          node.remove();
        }
      }),

      editor.registerNodeTransform(CollapsibleTitleNode, node => {
        const parent = node.getParent();
        if (!$isCollapsibleContainerNode(parent)) {
          node.replace($createParagraphNode().append(...node.getChildren()));
          return;
        }
      }),

      editor.registerNodeTransform(CollapsibleContainerNode, node => {
        const children = node.getChildren();
        if (
          children.length !== 2 ||
          !$isCollapsibleTitleNode(children[0]) ||
          !$isCollapsibleContentNode(children[1])
        ) {
          for (const child of children) {
            node.insertBefore(child);
          }
          node.remove();
        }
      }),

      // This handles the case when container is collapsed and we delete its previous sibling
      // into it, it would cause collapsed content deleted (since it's display: none, and selection
      // swallows it when deletes single char). Instead we expand container, which is although
      // not perfect, but avoids bigger problem
      editor.registerCommand(
        DELETE_CHARACTER_COMMAND,
        () => {
          const selection = $getSelection();
          if (
            !$isRangeSelection(selection) ||
            !selection.isCollapsed() ||
            selection.anchor.offset !== 0
          ) {
            return false;
          }

          const anchorNode = selection.anchor.getNode();
          const topLevelElement = anchorNode.getTopLevelElement();
          if (topLevelElement === null) {
            return false;
          }

          // If the caret is inside a collapsible (title or content), the
          // top-level element is the container itself. Backspace-from-inside
          // a collapsible is owned by the KEY_BACKSPACE_COMMAND handler
          // (moves caret to title / exits below on empty last line). Falling
          // through here would incorrectly treat the current collapsible as
          // an empty paragraph trailing the previous collapsible and remove
          // it.
          if ($isCollapsibleContainerNode(topLevelElement)) {
            return false;
          }

          const container = topLevelElement.getPreviousSibling();
          if (!$isCollapsibleContainerNode(container)) {
            return false;
          }

          if (!container.getOpen()) {
            container.setOpen(true);
            return true;
          }

          // Container is open and we're backspacing at the start of an empty
          // paragraph that follows it. Lexical's default deletion leaves the
          // selection on the container boundary, which breaks subsequent
          // backspace presses. Remove the paragraph ourselves and put the
          // cursor at the end of the collapsible's content instead.
          if (topLevelElement.getTextContent() !== '') {
            return false;
          }
          const contentNode = container.getLastChild();
          if (!$isCollapsibleContentNode(contentNode)) {
            return false;
          }
          const lastBlock = contentNode.getLastChild();
          if (!$isElementNode(lastBlock)) {
            return false;
          }

          topLevelElement.remove();
          lastBlock.selectEnd();
          return true;
        },
        COMMAND_PRIORITY_LOW,
      ),

      // When collapsible is the last child pressing down/right arrow will insert paragraph
      // below it to allow adding more content. It's similar what $insertBlockNode
      // (mainly for decorators), except it'll always be possible to continue adding
      // new content even if trailing paragraph is accidentally deleted
      editor.registerCommand(KEY_ARROW_DOWN_COMMAND, onEscapeDown, COMMAND_PRIORITY_LOW),

      editor.registerCommand(KEY_ARROW_RIGHT_COMMAND, onEscapeDown, COMMAND_PRIORITY_LOW),

      // When collapsible is the first child pressing up/left arrow will insert paragraph
      // above it to allow adding more content. It's similar what $insertBlockNode
      // (mainly for decorators), except it'll always be possible to continue adding
      // new content even if leading paragraph is accidentally deleted
      editor.registerCommand(KEY_ARROW_UP_COMMAND, onEscapeUp, COMMAND_PRIORITY_LOW),

      editor.registerCommand(KEY_ARROW_LEFT_COMMAND, onEscapeUp, COMMAND_PRIORITY_LOW),

      editor.registerCommand(KEY_BACKSPACE_COMMAND, onBackspace, COMMAND_PRIORITY_LOW),

      // Handling CMD+Enter to toggle collapsible element collapsed state
      editor.registerCommand(
        INSERT_PARAGRAPH_COMMAND,
        () => {
          const windowEvent = editor._window?.event;

          if (
            windowEvent &&
            (windowEvent.ctrlKey || windowEvent.metaKey) &&
            windowEvent.key === 'Enter'
          ) {
            const selection = $getPreviousSelection();
            if ($isRangeSelection(selection) && selection.isCollapsed()) {
              const parent = $findMatchingParent(
                selection.anchor.getNode(),
                node => $isElementNode(node) && !node.isInline(),
              );

              if ($isCollapsibleTitleNode(parent)) {
                const container = parent.getParent();
                if ($isCollapsibleContainerNode(container)) {
                  container.toggleOpen();
                  $setSelection(selection.clone());
                  return true;
                }
              }
            }
          }

          return false;
        },
        COMMAND_PRIORITY_LOW,
      ),

      // Enter inside a collapsible title: open the container (if closed) and
      // jump selection into the content's first block, instead of splitting
      // the title text or escaping the collapsible.
      editor.registerCommand(
        INSERT_PARAGRAPH_COMMAND,
        () => {
          const windowEvent = editor._window?.event;
          if (
            windowEvent &&
            (windowEvent.ctrlKey || windowEvent.metaKey) &&
            windowEvent.key === 'Enter'
          ) {
            return false;
          }

          const selection = $getSelection();
          if (!$isRangeSelection(selection)) return false;

          const titleNode = $findMatchingParent(
            selection.anchor.getNode(),
            $isCollapsibleTitleNode,
          );
          if (!$isCollapsibleTitleNode(titleNode)) return false;

          const container = titleNode.getParent();
          if (!$isCollapsibleContainerNode(container)) return false;

          if (!container.getOpen()) {
            container.setOpen(true);
          }

          const contentNode = titleNode.getNextSibling();
          if (!$isCollapsibleContentNode(contentNode)) return false;

          let target = contentNode.getFirstChild();
          if (!$isElementNode(target)) {
            target = $createParagraphNode();
            contentNode.append(target);
          }
          target.selectStart();
          // Clear the bold/other formats carried over from the title so
          // content typing starts at the content's default (non-bold).
          const contentSelection = $getSelection();
          if ($isRangeSelection(contentSelection)) {
            contentSelection.setFormat(0);
          }

          return true;
        },
        COMMAND_PRIORITY_LOW,
      ),
      editor.registerCommand(
        INSERT_COLLAPSIBLE_COMMAND,
        () => {
          const selectBoldTitleText = titleText => {
            const titleSelection = $createRangeSelection();
            titleSelection.setTextNodeRange(titleText, 0, titleText, 0);
            titleSelection.setFormat(IS_BOLD);
            titleSelection.setStyle(titleText.getStyle());
            $setSelection(titleSelection);
          };

          let titleTextKey = null;

          editor.update(() => {
            const selection = $getSelection();
            const titleParagraph = $createParagraphNode();
            titleParagraph.setTextFormat(IS_BOLD);
            const titleText = $createTextNode('');
            titleText.setFormat(IS_BOLD);
            titleTextKey = titleText.getKey();
            titleParagraph.append(titleText);
            const container = $createCollapsibleContainerNode(true).append(
              $createCollapsibleTitleNode().append(titleParagraph),
              $createCollapsibleContentNode().append($createParagraphNode()),
            );

            const anchorNode = $isRangeSelection(selection) ? selection.anchor.getNode() : null;

            // Walk up past any shadow roots (e.g. collapsible content) to the
            // node whose parent is the editor root, so the new collapsible is
            // always inserted as a sibling at root level, never nested inside
            // another collapsible.
            let rootLevelNode = null;
            let current = anchorNode;
            while (current) {
              const parent = current.getParent();
              if (parent === null) break;
              if ($isRootNode(parent)) {
                rootLevelNode = current;
                break;
              }
              current = parent;
            }

            if (rootLevelNode) {
              // If the caret is on an empty paragraph at the root, replace it
              // with the collapsible so no stray blank line is left above.
              if ($isParagraphNode(rootLevelNode) && rootLevelNode.getTextContent() === '') {
                rootLevelNode.replace(container);
              } else {
                rootLevelNode.insertAfter(container);
              }
            } else {
              const root = $getRoot();
              const lastChild = root.getLastChild();
              if ($isParagraphNode(lastChild) && lastChild.getTextContent() === '') {
                lastChild.replace(container);
              } else {
                root.append(container);
              }
            }

            selectBoldTitleText(titleText);
          });

          queueMicrotask(() => {
            editor.update(() => {
              const titleText = $getNodeByKey(titleTextKey);
              if (!$isTextNode(titleText)) {
                return;
              }

              selectBoldTitleText(titleText);
            });
          });

          return true;
        },
        COMMAND_PRIORITY_LOW,
      ),
    );
  }, [editor]);

  return null;
}
